import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  LinearProgress,
  Collapse,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  DateRange as DateRangeIcon,
  DataUsage as TokenIcon
} from '@mui/icons-material';
import trackingService from '../../services/trackingService';

// Helper to format date as YYYY-MM-DD
const formatDateString = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to format date for display
const formatDisplayDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Design System Colors (matching TrackingDashboard)
const colors = {
  primary: '#485869',
  primaryLight: '#6c7c8c',
  primaryDark: '#3A4754',
  success: '#66CC33',
  error: '#fa5b5b',
  warning: '#FF9933',
  info: '#4DBCF3',
  background: '#F5F6F7',
  paper: '#FFFFFF',
  textPrimary: '#485869',
  textSecondary: '#6B757F',
  grey50: '#F5F6F7',
  grey200: '#C8CDD2',
  grey500: '#6B757F',
  grey700: '#485869',
};

// Stats Card Component
const StatCard = ({ title, value, subtitle, icon, color = colors.primary, loading = false }) => (
  <Card
    elevation={2}
    sx={{
      height: '100%',
      bgcolor: colors.paper,
      borderRadius: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': {
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)'
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            {title}
          </Typography>
          {loading ? (
            <CircularProgress size={24} sx={{ mt: 1 }} />
          ) : (
            <>
              <Typography variant="h4" sx={{ color, fontWeight: 700, mt: 1 }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {subtitle}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.6, fontSize: 40 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Format number with commas
const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString();
};

// Format cost in dollars
const formatCost = (cents) => {
  if (!cents) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

// User Row Component for the usage table
const UserRow = ({ user, identifier }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: colors.grey50 },
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell sx={{ width: 50 }}>
          <IconButton size="small" sx={{ color: colors.textSecondary }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: colors.primary, fontSize: 20 }} />
            <Typography variant="body2" fontWeight={600} color={colors.textPrimary}>
              {identifier}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={colors.textPrimary}>
            {formatNumber(user.sessions)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={colors.textPrimary}>
            {formatNumber(user.input_tokens)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={colors.textPrimary}>
            {formatNumber(user.output_tokens)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip
            label={formatCost(user.estimated_cost_cents)}
            size="small"
            sx={{
              bgcolor: `${colors.success}20`,
              color: colors.success,
              fontWeight: 600
            }}
          />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={colors.textPrimary}>
            {formatNumber(user.commits)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={colors.textPrimary}>
            {formatNumber(user.pull_requests)}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, borderBottom: expanded ? `1px solid ${colors.grey200}` : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: colors.grey50 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color={colors.textSecondary}>Lines Added</Typography>
                  <Typography variant="body2" color={colors.success} fontWeight={600}>
                    +{formatNumber(user.lines_added)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color={colors.textSecondary}>Lines Removed</Typography>
                  <Typography variant="body2" color={colors.error} fontWeight={600}>
                    -{formatNumber(user.lines_removed)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color={colors.textSecondary}>Total Tokens</Typography>
                  <Typography variant="body2" color={colors.textPrimary} fontWeight={600}>
                    {formatNumber(user.input_tokens + user.output_tokens)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color={colors.textSecondary}>Actor Type</Typography>
                  <Typography variant="body2" color={colors.textPrimary} fontWeight={600}>
                    {user.type === 'user_actor' ? 'User' : 'API Key'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Model Usage Component
const ModelUsageCard = ({ modelUsage }) => {
  if (!modelUsage || Object.keys(modelUsage).length === 0) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, p: 2 }}>
        <Typography variant="body2" color={colors.textSecondary}>
          No model usage data available
        </Typography>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary, mb: 2 }}>
          Model Usage Breakdown
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Model</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: colors.textSecondary }}>Input</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: colors.textSecondary }}>Output</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: colors.textSecondary }}>Cache Read</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: colors.textSecondary }}>Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(modelUsage).map(([model, usage]) => (
                <TableRow key={model}>
                  <TableCell>
                    <Chip
                      label={model}
                      size="small"
                      sx={{ bgcolor: `${colors.info}20`, color: colors.info, fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell align="right">{formatNumber(usage.input_tokens)}</TableCell>
                  <TableCell align="right">{formatNumber(usage.output_tokens)}</TableCell>
                  <TableCell align="right">{formatNumber(usage.cache_read_tokens)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: colors.success, fontWeight: 600 }}>
                      {formatCost(usage.estimated_cost_cents)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// Tool Usage Component
const ToolUsageCard = ({ toolUsage }) => {
  if (!toolUsage || Object.keys(toolUsage).length === 0) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, p: 2 }}>
        <Typography variant="body2" color={colors.textSecondary}>
          No tool usage data available
        </Typography>
      </Card>
    );
  }

  // Sort by total actions (accepted + rejected)
  const sortedTools = Object.entries(toolUsage)
    .map(([name, data]) => ({
      name,
      accepted: data.accepted || 0,
      rejected: data.rejected || 0,
      total: (data.accepted || 0) + (data.rejected || 0),
      acceptRate: data.accepted ? Math.round((data.accepted / ((data.accepted || 0) + (data.rejected || 0))) * 100) : 0
    }))
    .sort((a, b) => b.total - a.total);

  return (
    <Card elevation={1} sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary, mb: 2 }}>
          Tool Usage & Acceptance Rates
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sortedTools.slice(0, 10).map((tool) => (
            <Box key={tool.name}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600} color={colors.textPrimary}>
                  {tool.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Tooltip title="Accepted">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckIcon sx={{ fontSize: 14, color: colors.success }} />
                      <Typography variant="caption" color={colors.success}>{tool.accepted}</Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Rejected">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CancelIcon sx={{ fontSize: 14, color: colors.error }} />
                      <Typography variant="caption" color={colors.error}>{tool.rejected}</Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={tool.acceptRate}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: colors.grey200,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: tool.acceptRate > 80 ? colors.success : tool.acceptRate > 50 ? colors.warning : colors.error,
                      borderRadius: 3
                    }
                  }}
                />
                <Typography variant="caption" color={colors.textSecondary} sx={{ minWidth: 35 }}>
                  {tool.acceptRate}%
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// Main ClaudeUsageTab Component
const ClaudeUsageTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('weekly'); // 'daily' or 'weekly'
  const [selectedDate, setSelectedDate] = useState(formatDateString(new Date()));
  const [usageData, setUsageData] = useState(null);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(true);

  // Fetch usage data
  const fetchUsageData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError('');

    try {
      let data;
      if (viewMode === 'daily') {
        data = await trackingService.getClaudeUsage(selectedDate, forceRefresh);
      } else {
        data = await trackingService.getClaudeUsageWeekly(forceRefresh);
      }
      setUsageData(data);
      setApiKeyConfigured(true);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch usage data';
      if (errorMessage.includes('API key') || errorMessage.includes('configured')) {
        setApiKeyConfigured(false);
        setError('Anthropic Admin API key not configured. Please set ANTHROPIC_ADMIN_API_KEY in your .env file.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedDate]);

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  // Get summary data based on view mode
  const getSummary = () => {
    if (!usageData) return null;
    if (viewMode === 'daily') {
      return usageData.summary || {};
    }
    return usageData.aggregated || {};
  };

  const summary = getSummary();
  const userBreakdown = viewMode === 'weekly'
    ? usageData?.aggregated?.user_breakdown || {}
    : {};

  // For daily view, extract users from records
  const dailyUsers = viewMode === 'daily' && usageData?.records
    ? usageData.records.reduce((acc, record) => {
        const id = record.actor?.identifier;
        if (!id) return acc;
        if (!acc[id]) {
          acc[id] = {
            type: record.actor?.type,
            sessions: 0,
            input_tokens: 0,
            output_tokens: 0,
            estimated_cost_cents: 0,
            commits: 0,
            pull_requests: 0,
            lines_added: 0,
            lines_removed: 0
          };
        }
        const core = record.core_metrics || {};
        acc[id].sessions += core.num_sessions || 0;
        acc[id].commits += core.commits_by_claude_code || 0;
        acc[id].pull_requests += core.pull_requests_by_claude_code || 0;
        acc[id].lines_added += core.lines_added || 0;
        acc[id].lines_removed += core.lines_removed || 0;

        (record.model_breakdown || []).forEach(m => {
          acc[id].input_tokens += m.tokens?.input || 0;
          acc[id].output_tokens += m.tokens?.output || 0;
          acc[id].estimated_cost_cents += m.estimated_cost?.amount || 0;
        });
        return acc;
      }, {})
    : {};

  const users = viewMode === 'weekly' ? userBreakdown : dailyUsers;
  const modelUsage = viewMode === 'weekly'
    ? usageData?.aggregated?.model_usage_summary
    : usageData?.model_usage_summary;
  const toolUsage = viewMode === 'weekly'
    ? usageData?.aggregated?.tool_usage_summary
    : usageData?.tool_usage_summary;

  return (
    <Box>
      {/* Header Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                px: 2
              },
              '& .Mui-selected': {
                bgcolor: `${colors.primary} !important`,
                color: 'white !important'
              }
            }}
          >
            <ToggleButton value="weekly">
              <DateRangeIcon sx={{ mr: 1, fontSize: 18 }} />
              Last 7 Days
            </ToggleButton>
            <ToggleButton value="daily">
              <CalendarIcon sx={{ mr: 1, fontSize: 18 }} />
              Single Day
            </ToggleButton>
          </ToggleButtonGroup>

          {viewMode === 'daily' && (
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              inputProps={{ max: formatDateString(new Date()) }}
              size="small"
              sx={{ width: 180 }}
            />
          )}
        </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchUsageData(true)}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primaryDark,
                bgcolor: `${colors.primary}10`
              }
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity={apiKeyConfigured ? 'error' : 'warning'} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && !usageData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        )}

        {/* Main Content */}
        {!loading && usageData && (
          <>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Sessions"
                  value={formatNumber(summary.total_sessions)}
                  subtitle={viewMode === 'weekly' ? 'Last 7 days' : formatDisplayDate(selectedDate)}
                  icon={<SpeedIcon fontSize="inherit" />}
                  color={colors.primary}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Active Users"
                  value={formatNumber(summary.total_users)}
                  icon={<PersonIcon fontSize="inherit" />}
                  color={colors.info}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Tokens"
                  value={formatNumber((summary.total_input_tokens || 0) + (summary.total_output_tokens || 0))}
                  subtitle={`In: ${formatNumber(summary.total_input_tokens)} / Out: ${formatNumber(summary.total_output_tokens)}`}
                  icon={<TokenIcon fontSize="inherit" />}
                  color={colors.warning}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Estimated Cost"
                  value={formatCost(summary.total_estimated_cost_cents)}
                  icon={<MoneyIcon fontSize="inherit" />}
                  color={colors.success}
                />
              </Grid>
            </Grid>

            {/* Secondary Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={3}>
                <Card elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color={colors.textSecondary}>Commits</Typography>
                  <Typography variant="h6" color={colors.textPrimary} fontWeight={700}>
                    {formatNumber(summary.total_commits)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color={colors.textSecondary}>Pull Requests</Typography>
                  <Typography variant="h6" color={colors.textPrimary} fontWeight={700}>
                    {formatNumber(summary.total_pull_requests)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color={colors.textSecondary}>Lines Added</Typography>
                  <Typography variant="h6" color={colors.success} fontWeight={700}>
                    +{formatNumber(summary.total_lines_added)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color={colors.textSecondary}>Lines Removed</Typography>
                  <Typography variant="h6" color={colors.error} fontWeight={700}>
                    -{formatNumber(summary.total_lines_removed)}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Model and Tool Usage */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <ModelUsageCard modelUsage={modelUsage} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToolUsageCard toolUsage={toolUsage} />
              </Grid>
            </Grid>

            {/* User Breakdown Table */}
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary, mb: 2 }}>
              User Breakdown
            </Typography>
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                borderRadius: 2,
                maxHeight: 'calc(100vh - 600px)',
                minHeight: 300
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: colors.grey50, width: 50 }}></TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, fontWeight: 600, color: colors.textSecondary }}>User / API Key</TableCell>
                    <TableCell align="right" sx={{ bgcolor: colors.grey50, fontWeight: 600, color: colors.textSecondary }}>Sessions</TableCell>
                    <TableCell align="right" sx={{ bgcolor: colors.grey50, fontWeight: 600, color: colors.textSecondary }}>Input Tokens</TableCell>
                    <TableCell align="right" sx={{ bgcolor: colors.grey50, fontWeight: 600, color: colors.textSecondary }}>Output Tokens</TableCell>
                    <TableCell align="right" sx={{ bgcolor: colors.grey50, fontWeight: 600, color: colors.textSecondary }}>Cost</TableCell>
                    <TableCell align="right" sx={{ bgcolor: colors.grey50, fontWeight: 600, color: colors.textSecondary }}>Commits</TableCell>
                    <TableCell align="right" sx={{ bgcolor: colors.grey50, fontWeight: 600, color: colors.textSecondary }}>PRs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(users)
                    .sort((a, b) => (b[1].estimated_cost_cents || 0) - (a[1].estimated_cost_cents || 0))
                    .map(([identifier, user]) => (
                      <UserRow key={identifier} identifier={identifier} user={user} />
                    ))}
                  {Object.keys(users).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ color: colors.textSecondary, py: 6 }}>
                        No usage data available for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Cache Info */}
            {usageData.fetched_at && (
              <Typography variant="caption" color={colors.textSecondary} sx={{ mt: 2, display: 'block' }}>
                Data cached at: {new Date(usageData.fetched_at).toLocaleString()}
              </Typography>
            )}
          </>
        )}

        {/* No API Key Configured State */}
        {!loading && !apiKeyConfigured && (
          <Card elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <CodeIcon sx={{ fontSize: 64, color: colors.grey500, mb: 2 }} />
            <Typography variant="h6" color={colors.textPrimary} gutterBottom>
              API Key Required
            </Typography>
            <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 2 }}>
              To view Claude Code usage metrics, you need to configure your Anthropic Admin API key.
            </Typography>
            <Typography variant="body2" color={colors.textSecondary}>
              Add <code>ANTHROPIC_ADMIN_API_KEY=your_key_here</code> to your backend <code>.env</code> file.
            </Typography>
          </Card>
        )}
      </Box>
  );
};

export default ClaudeUsageTab;
